/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import Button from '@atlaskit/button/new';
import Checkbox from '@atlaskit/checkbox';
import { cssMap, jsx } from '@atlaskit/css';
import { Label } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/core/add';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import InlineEdit from '@atlaskit/inline-edit';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import type { InputActionMeta } from '@atlaskit/react-select';
import Select from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';

import type { KbTypeOption } from './CreateKBCardForm';
import { createKbMessages } from './messages';

export type SpaceOption = {
	label: string;
	value: string;
	key: string;
};

type ConfluenceSpace = {
	id: string;
	key: string;
	name: string;
};

const mapToSpaceOptions = (spaces: ConfluenceSpace[]): SpaceOption[] =>
	spaces
		.map(({ id, key, name }) => ({
			label: name,
			value: id,
			key,
		}))
		.filter(({ value }) => Boolean(value));

const styles = cssMap({
	formBody: {
		paddingTop: token('space.200'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.250'),
	},
	infoRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.050'),
	},
	typeLabelRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
	inlineEditReadView: {
		minHeight: '2rem',
	},
});

type CreateKBCardPublishFormProps = {
	selectedType: KbTypeOption;
	onSelectedTypeChange: (option: KbTypeOption) => void;
	selectedSpace: SpaceOption | null;
	appLinkId: string | null;
	prefetchedSpaceOptions?: SpaceOption[] | null;
	onSelectedSpaceChange: (option: SpaceOption | null) => void;
	onPublishConsentChange: (state: {
		isRequired: boolean;
		isChecked: boolean;
		isSpaceNameValid: boolean;
	}) => void;
	onPublishTargetChange: (state: {
		newSpaceName: string | null;
		isNewSpaceConfirmed: boolean;
	}) => void;
	cloudId: string;
	canCreateSpace: boolean;
	isFetchingCreateSpacePermission: boolean;
	userId?: string;
	isPrefetchedSpaceOptionsLoading: boolean;
	hasLinkSourceAlreadyHasContentError?: boolean;
	hasGenericActionError?: boolean;
};

export const CreateKBCardPublishForm = ({
	selectedType,
	onSelectedTypeChange,
	selectedSpace,
	appLinkId,
	prefetchedSpaceOptions,
	onSelectedSpaceChange,
	onPublishConsentChange,
	onPublishTargetChange,
	userId,
	cloudId,
	canCreateSpace,
	isFetchingCreateSpacePermission,
	isPrefetchedSpaceOptionsLoading,
	hasLinkSourceAlreadyHasContentError = false,
	hasGenericActionError = false,
}: CreateKBCardPublishFormProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { listEntities } = useConfluenceEntities(cloudId);
	const hasAppLinkId = Boolean(appLinkId);
	const [isCreateSpaceInlineEditVisible, setIsCreateSpaceInlineEditVisible] = useState(false);
	const [isNewSpaceConfirmed, setIsNewSpaceConfirmed] = useState(false);
	const [confirmedSpaceName, setConfirmedSpaceName] = useState('');
	const [isPublishToNewSpaceChecked, setIsPublishToNewSpaceChecked] = useState(false);
	const [isSpaceOptionsLoading, setIsSpaceOptionsLoading] = useState(
		isPrefetchedSpaceOptionsLoading,
	);
	const shouldShowPublishToNewSpaceCheckbox = !appLinkId;
	const [searchInputValue, setSearchInputValue] = useState('');
	const latestSearchRequestIdRef = useRef(0);
	const baseSpaceOptionsRef = useRef<SpaceOption[]>(prefetchedSpaceOptions ?? []);

	const typeOptions = useMemo<KbTypeOption[]>(
		() => [
			{ label: formatMessage(createKbMessages.typeInternalMessage), value: 'INTERNAL' },
			{ label: formatMessage(createKbMessages.typeExternalMessage), value: 'EXTERNAL' },
		],
		[formatMessage],
	);
	const externalTypeOption = useMemo<KbTypeOption>(
		() => typeOptions.find((option) => option.value === 'EXTERNAL') ?? typeOptions[0],
		[typeOptions],
	);

	const [spaceOptions, setSpaceOptions] = useState<SpaceOption[]>(prefetchedSpaceOptions ?? []);
	const fetchSpaces = useCallback(
		async (query: string): Promise<ConfluenceSpace[]> => {
			const entities = await listEntities(query, ['space']);
			return entities.reduce<ConfluenceSpace[]>((spaces, entity) => {
				if (!entity.id || !entity.key) {
					return spaces;
				}

				spaces.push({
					id: entity.id,
					key: entity.key,
					name: entity.name,
				});
				return spaces;
			}, []);
		},
		[listEntities],
	);
	const [debouncedSearchSpaces, cancelDebouncedSearchSpaces] = useDebouncedCallback(
		async (searchInput: string, requestId: number) => {
			setIsSpaceOptionsLoading(true);
			try {
				const matchedSpaces = await fetchSpaces(searchInput);
				if (requestId !== latestSearchRequestIdRef.current) {
					return;
				}

				setSpaceOptions(mapToSpaceOptions(matchedSpaces));
				setIsSpaceOptionsLoading(false);
			} catch {
				if (requestId !== latestSearchRequestIdRef.current) {
					return;
				}

				setIsSpaceOptionsLoading(false);
			}
		},
		300,
	);

	const handleCreateSpaceConfirm = (spaceName: string) => {
		const trimmedSpaceName = spaceName.trim();
		if (!trimmedSpaceName) {
			setIsCreateSpaceInlineEditVisible(false);
			return;
		}

		setConfirmedSpaceName(trimmedSpaceName);
		setIsNewSpaceConfirmed(true);
		setIsCreateSpaceInlineEditVisible(false);
		onPublishTargetChange({ newSpaceName: spaceName, isNewSpaceConfirmed: true });
	};

	useEffect(() => {
		onPublishConsentChange({
			isRequired: isNewSpaceConfirmed && shouldShowPublishToNewSpaceCheckbox,
			isChecked: shouldShowPublishToNewSpaceCheckbox ? isPublishToNewSpaceChecked : true,
			isSpaceNameValid: confirmedSpaceName.trim().length > 0,
		});
	}, [
		confirmedSpaceName,
		isNewSpaceConfirmed,
		isPublishToNewSpaceChecked,
		onPublishConsentChange,
		shouldShowPublishToNewSpaceCheckbox,
	]);

	useEffect(() => {
		onPublishTargetChange({
			newSpaceName: isNewSpaceConfirmed ? confirmedSpaceName.trim() : null,
			isNewSpaceConfirmed,
		});
	}, [confirmedSpaceName, isNewSpaceConfirmed, onPublishTargetChange]);

	useEffect(() => {
		if (!hasAppLinkId && selectedType.value !== 'EXTERNAL') {
			onSelectedTypeChange(externalTypeOption);
		}
	}, [externalTypeOption, hasAppLinkId, onSelectedTypeChange, selectedType.value]);

	useEffect(() => {
		if (!userId) {
			setSpaceOptions([]);
			onSelectedSpaceChange(null);
			return;
		}

		const applySpaceOptions = (nextSpaceOptions: SpaceOption[], syncSelection = true) => {
			setSpaceOptions(nextSpaceOptions);
			if (syncSelection) {
				baseSpaceOptionsRef.current = nextSpaceOptions;
			}

			if (!syncSelection) {
				return;
			}

			// Preserve explicit user selections (including searched results) even when
			// the base option list is reapplied.
			if (!selectedSpace) {
				onSelectedSpaceChange(nextSpaceOptions[0] ?? null);
			}
		};

		if (prefetchedSpaceOptions !== undefined) {
			if (prefetchedSpaceOptions === null) {
				setIsSpaceOptionsLoading(true);
				return;
			}

			applySpaceOptions(prefetchedSpaceOptions);
			setIsSpaceOptionsLoading(false);
			return;
		}

		let isCancelled = false;
		const loadSpaces = async () => {
			setIsSpaceOptionsLoading(true);
			const spaces = await fetchSpaces('');
			if (isCancelled) {
				return;
			}

			applySpaceOptions(mapToSpaceOptions(spaces));
			setIsSpaceOptionsLoading(false);
		};

		void loadSpaces();
		return () => {
			isCancelled = true;
		};
	}, [fetchSpaces, onSelectedSpaceChange, prefetchedSpaceOptions, selectedSpace, userId]);

	useEffect(() => {
		const normalizedSearchInput = searchInputValue.trim();
		if (!userId || normalizedSearchInput.length === 0) {
			cancelDebouncedSearchSpaces();
			setSpaceOptions(baseSpaceOptionsRef.current);
			setIsSpaceOptionsLoading(false);
			return;
		}

		const requestId = latestSearchRequestIdRef.current + 1;
		latestSearchRequestIdRef.current = requestId;
		debouncedSearchSpaces(normalizedSearchInput, requestId);

		return () => {
			cancelDebouncedSearchSpaces();
		};
	}, [cancelDebouncedSearchSpaces, debouncedSearchSpaces, searchInputValue, userId]);

	const handleSpaceInputChange = (value: string, actionMeta: InputActionMeta): string => {
		if (actionMeta.action === 'input-change') {
			setSearchInputValue(value);
		}

		if (actionMeta.action === 'menu-close' || actionMeta.action === 'input-blur') {
			setSearchInputValue('');
		}

		return value;
	};

	const isCreateSpaceButtonDisabled = isFetchingCreateSpacePermission || !canCreateSpace;
	const showCreateSpacePermissionTooltip =
		hasAppLinkId &&
		!isFetchingCreateSpacePermission &&
		!canCreateSpace &&
		fg('jsm_kb_from_scratch_check_space_creation');
	const createSpacePermissionTooltip = formatMessage(
		createKbMessages.createSpaceDisabledTooltipMessage,
	);
	const createSpacePermissionAriaLabel = showCreateSpacePermissionTooltip
		? `${formatMessage(createKbMessages.createSpaceMessage)}. ${createSpacePermissionTooltip}`
		: undefined;

	return (
		<Box testId="create-kb-card-publish-form" xcss={styles.formBody}>
			<Stack space="space.200">
				{hasAppLinkId ? (
					<Stack space="space.050">
						<Box xcss={styles.typeLabelRow}>
							<Label htmlFor="create-kb-publish-type">
								{formatMessage(createKbMessages.typeLabelMessage)}
							</Label>
							<Tooltip content={formatMessage(createKbMessages.typeTooltipMessage)}>
								<InformationCircleIcon
									label={formatMessage(createKbMessages.typeInfoIconLabelMessage)}
									color={token('color.icon.subtle')}
								/>
							</Tooltip>
						</Box>
						<Select<KbTypeOption>
							inputId="create-kb-publish-type"
							options={typeOptions}
							value={selectedType}
							onChange={(option) => option && onSelectedTypeChange(option)}
							testId="create-kb-publish-type"
						/>
					</Stack>
				) : null}

				<Stack space="space.050">
					{!hasAppLinkId ? (
						<Stack space="space.150">
							<Stack space="space.050">
								<Label htmlFor="create-kb-publish-space-name">
									{formatMessage(createKbMessages.nameLabelMessage)}
								</Label>
								<Textfield
									id="create-kb-publish-space-name"
									value={confirmedSpaceName}
									onChange={(event) => {
										const nextSpaceName = (event.target as HTMLInputElement).value;
										setConfirmedSpaceName(nextSpaceName);
										setIsNewSpaceConfirmed(nextSpaceName.trim().length > 0);
									}}
									testId="create-kb-publish-space-name"
									autoFocus
								/>
							</Stack>
							{shouldShowPublishToNewSpaceCheckbox ? (
								<Checkbox
									isChecked={isPublishToNewSpaceChecked}
									onChange={(event) => {
										setIsPublishToNewSpaceChecked((event.target as HTMLInputElement).checked);
									}}
									label={formatMessage(createKbMessages.publishToNewSpaceCheckboxLabelMessage)}
									testId="create-kb-card-publish-to-new-space-checkbox"
								/>
							) : null}
						</Stack>
					) : isNewSpaceConfirmed ? (
						<Stack space="space.150">
							<Stack space="space.050">
								<Label htmlFor="create-kb-publish-space-name">
									{formatMessage(createKbMessages.nameLabelMessage)}
								</Label>
								<Textfield
									id="create-kb-publish-space-name"
									value={confirmedSpaceName}
									onChange={(event) =>
										setConfirmedSpaceName((event.target as HTMLInputElement).value)
									}
									testId="create-kb-publish-space-name"
									autoFocus
								/>
							</Stack>
							{shouldShowPublishToNewSpaceCheckbox ? (
								<Checkbox
									isChecked={isPublishToNewSpaceChecked}
									onChange={(event) => {
										setIsPublishToNewSpaceChecked((event.target as HTMLInputElement).checked);
									}}
									label={formatMessage(createKbMessages.publishToNewSpaceCheckboxLabelMessage)}
									testId="create-kb-card-publish-to-new-space-checkbox"
								/>
							) : null}
						</Stack>
					) : (
						<Box>
							{hasAppLinkId ? (
								<Box>
									<Label htmlFor="create-kb-publish-space">
										{formatMessage(createKbMessages.selectSpaceLabelMessage)}
									</Label>
									<Select<SpaceOption>
										inputId="create-kb-publish-space"
										options={spaceOptions ?? []}
										value={selectedSpace}
										onChange={(option) => onSelectedSpaceChange(option)}
										testId="create-kb-publish-space"
										isLoading={isSpaceOptionsLoading}
										onInputChange={handleSpaceInputChange}
										menuPosition="fixed"
									/>
								</Box>
							) : null}
							<Box>
								{isCreateSpaceInlineEditVisible ? (
									<InlineEdit
										startWithEditViewOpen
										defaultValue=""
										label={formatMessage(createKbMessages.nameLabelMessage)}
										onConfirm={handleCreateSpaceConfirm}
										onCancel={() => setIsCreateSpaceInlineEditVisible(false)}
										readView={() => <Box xcss={styles.inlineEditReadView} />}
										editView={({ errorMessage: _errorMessage, ...fieldProps }, ref) => (
											<Textfield
												{...fieldProps}
												ref={ref}
												testId="create-kb-card-create-space-inline-edit-input"
												autoFocus
											/>
										)}
									/>
								) : (
									<Tooltip
										content={showCreateSpacePermissionTooltip ? createSpacePermissionTooltip : null}
										canAppear={() => showCreateSpacePermissionTooltip}
									>
										<Button
											appearance="subtle"
											onClick={() => setIsCreateSpaceInlineEditVisible(true)}
											testId="create-kb-card-create-space"
											iconBefore={AddIcon}
											isDisabled={isCreateSpaceButtonDisabled}
											aria-label={createSpacePermissionAriaLabel}
										>
											{formatMessage(createKbMessages.createSpaceMessage)}
										</Button>
									</Tooltip>
								)}
							</Box>
						</Box>
					)}
				</Stack>

				{hasLinkSourceAlreadyHasContentError ? (
					<Box xcss={styles.infoRow}>
						<StatusErrorIcon label="" color={token('color.icon.danger')} />
						<Text size="medium" color="color.text">
							{formatMessage(createKbMessages.publishFormLinkingErrorMessage)}
						</Text>
					</Box>
				) : hasGenericActionError ? (
					<Box xcss={styles.infoRow}>
						<StatusErrorIcon label="" color={token('color.icon.danger')} />
						<Text size="medium" color="color.text">
							{formatMessage(createKbMessages.genericActionErrorMessage)}
						</Text>
					</Box>
				) : !isNewSpaceConfirmed ? (
					<Box xcss={styles.infoRow}>
						<StatusInformationIcon label="" color={token('color.icon.subtle')} />
						<Text size="medium" color="color.text">
							{formatMessage(createKbMessages.publishFormDescriptionMessage)}
						</Text>
					</Box>
				) : null}
			</Stack>
		</Box>
	);
};
